import { createProjectScopedStore } from './projectScoped';
import { http } from '@/lib/http';
import { measure } from '@/lib/performance';
import { toast } from 'sonner';

export interface GitFileStatus {
    path: string;
    status: string;
}

export interface GitStatus {
    staged: GitFileStatus[];
    unstaged: GitFileStatus[];
    untracked: GitFileStatus[];
}

type GitState = {
    status: GitStatus | null;
    aheadBehind: { ahead: number; behind: number } | null;
    commitMessage: string;
    isLoading: boolean;
    isGeneratingCommitMessage: boolean;
};

type GitActions = {
    fetchStatus: () => Promise<void>;
    fetchAheadBehind: () => Promise<void>;
    syncWithOrigin: () => Promise<void>;
    stageFiles: (files: string[]) => Promise<void>;
    unstageFiles: (files: string[]) => Promise<void>;
    discardUnstagedChanges: (files: string[]) => Promise<void>;
    discardUntrackedFiles: (files: string[]) => Promise<void>;
    commit: () => Promise<void>;
    createProposalFromStaged: (providerId?: string) => Promise<void>;
    setCommitMessage: (message: string) => void;
};

const initialState: GitState = {
    status: null,
    aheadBehind: null,
    commitMessage: '',
    isLoading: false,
    isGeneratingCommitMessage: false,
};

export const useGitStore = createProjectScopedStore<GitState & GitActions>('git', (set, get) => ({
    ...initialState,
    setCommitMessage: (message) => set({ commitMessage: message }),
    fetchStatus: measure(async () => {
        set({ isLoading: true });
        try {
            const status = await http<GitStatus>('/api/git/status');
            set({ status });
            await get().fetchAheadBehind();
        } catch (error: any) {
            toast.error("Failed to get Git status", { description: error.message });
            set({ isLoading: false });
        } finally {
            set({ isLoading: false });
        }
    }, 'git.fetchStatus'),
    fetchAheadBehind: measure(async () => {
        try {
            const data = await http<{ ahead: number; behind: number } | null>('/api/git/ahead-behind');
            set({ aheadBehind: data });
        } catch (error: any) {
            // This can fail gracefully if there's no remote, so don't show a toast.
            console.error("Failed to get ahead/behind status:", error.message);
            set({ aheadBehind: null });
        }
    }, 'git.fetchAheadBehind'),
    syncWithOrigin: measure(async () => {
        set({ isLoading: true });
        const toastId = toast.loading("Syncing with origin...");
        try {
            const response = await http<{ message: string }>('/api/git/sync', { method: 'POST' });
            toast.success(response.message, { id: toastId });
            await get().fetchStatus(); // Refresh status and ahead/behind after sync
        } catch (error: any) {
            toast.error("Failed to sync with origin", { id: toastId, description: error.message });
            await get().fetchStatus(); // Also refresh on failure
        } finally {
            set({ isLoading: false });
        }
    }, 'git.syncWithOrigin'),
    stageFiles: measure(async (files) => {
        try {
            await http('/api/git/stage', {
                method: 'POST',
                body: JSON.stringify({ files }),
            });
            await get().fetchStatus();
        } catch (error: any) {
            toast.error("Failed to stage files", { description: error.message });
        }
    }, 'git.stageFiles'),
    unstageFiles: measure(async (files) => {
        try {
            await http('/api/git/unstage', {
                method: 'POST',
                body: JSON.stringify({ files }),
            });
            await get().fetchStatus();
        } catch (error: any) {
            toast.error("Failed to unstage files", { description: error.message });
        }
    }, 'git.unstageFiles'),
    discardUnstagedChanges: measure(async (files) => {
        if (files.length === 0) return;
        try {
            await http('/api/git/checkout', {
                method: 'POST',
                body: JSON.stringify({ files }),
            });
            await get().fetchStatus();
        } catch (error: any) {
            toast.error("Failed to discard changes", { description: error.message });
        }
    }, 'git.discardUnstagedChanges'),
    discardUntrackedFiles: measure(async (files) => {
        if (files.length === 0) return;
        try {
            await http('/api/git/clean', {
                method: 'POST',
                body: JSON.stringify({ files }),
            });
            await get().fetchStatus();
        } catch (error: any) {
            toast.error("Failed to discard untracked files", { description: error.message });
        }
    }, 'git.discardUntrackedFiles'),
    commit: measure(async () => {
        const { commitMessage } = get();
        if (!commitMessage.trim()) {
            toast.error("Commit message cannot be empty.");
            return;
        }
        try {
            await http('/api/git/commit', {
                method: 'POST',
                body: JSON.stringify({ message: commitMessage }),
            });
            toast.success("Commit successful!");
            set({ commitMessage: '' });
            await get().fetchStatus();
        } catch (error: any) {
            toast.error("Commit failed", { description: error.message });
        }
    }, 'git.commit'),
    createProposalFromStaged: measure(async (providerId?: string) => {
        const { commitMessage: instructions } = get();
        set({ isGeneratingCommitMessage: true });
        const toastId = toast.loading("Generating commit message...");
        try {
            const response = await http<{ commitMessage: string }>('/api/git/commit-message', {
                method: 'POST',
                body: JSON.stringify({
                    instructions: instructions,
                    providerId: providerId,
                }),
            });
            if (response.commitMessage) {
                set({ commitMessage: response.commitMessage });
                toast.success("Commit message generated.", { id: toastId });
            } else {
                throw new Error("Empty commit message from provider.");
            }
        } catch (error: any) {
            toast.error("Failed to generate commit message", { id: toastId, description: error.message });
        } finally {
            set({ isGeneratingCommitMessage: false });
        }
    }, 'git.createProposalFromStaged')
}));